import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BaseCommentInput } from "@/components/common/BaseCommentInput";
import { ExpandableText } from "@/components/common/ExpandableText";
import type { EditableFileOps } from "@/components/common/hooks/useEditableAttachments";
import { MediaPreview } from "@/components/common/MediaPreview";
import { COMMENT_MENTION_PAGE_SIZE } from "@/components/common/mentionSuggestionLimits";
import { SendingBadge } from "@/components/common/SendingBadge";
import { TranslationToggleButton } from "@/components/common/TranslationToggleButton";
import { UserAvatarWithPopup } from "@/components/common/UserAvatarWithPopup";
import { Card, CardContent } from "@/components/ui/card";
import { EventLinkPreviewProvider } from "@/contexts/EventLinkPreviewContext";
import { draftRepository } from "@/features/composer-drafts/draftRepository";
import type { ComposerDraftScope } from "@/features/composer-drafts/types";
import { ActivityStatusBadge } from "@/features/study-group-stream/components/ActivityStatusBadge";
import { CommentEditForm } from "@/features/study-group-stream/components/CommentEditForm";
import { CommentHeaderActions } from "@/features/study-group-stream/components/CommentHeaderActions";
import { InteractionFooter } from "@/features/study-group-stream/components/InteractionFooter";
import { ReplyCard } from "@/features/study-group-stream/components/ReplyCard";
import {
  getEffectiveIsSending,
  getIsAnyActionLoading,
} from "@/features/study-group-stream/utils/commentUiState";
import { COMMENT_REACTION_OBJECT_MODEL } from "@/hooks/useCommentReactions";
import { useMentionInputNotice } from "@/hooks/useMentionInputNotice";
import { useOriginalContentToggle } from "@/hooks/useOriginalContentToggle";
import { useStudyGroupMentionAvailableUsers } from "@/hooks/useStudyGroupMentionAvailableUsers";
import { toast } from "@/hooks/useToast";
import { getApiErrorStatus } from "@/lib/apiErrors";
import type { Comment, ReactionType } from "@/lib/humhub-api/types";
import { cn } from "@/lib/utils";
import { convertTimezoneToLocal } from "@/lib/utils/dateUtils";

interface CommentCardProps {
  author?: string;
  content?: string;
  timestamp?: string;
  avatarUrl?: string | null;
  isInput?: boolean;
  postId?: number;
  /** Content id of the containing post, distinct from the comment API target. */
  draftPostId?: number | string | null;
  replyParentCommentId?: number | string | null;
  replyAnchorCommentId?: number | string | null;
  commentId?: number;
  currentReaction?: ReactionType | null;
  onReaction?: (
    commentId: number,
    reaction: ReactionType | null,
  ) => Promise<void>;
  onAddComment?: (
    content: string,
    objectId: number | string,
    objectModel?: "post" | "comment",
    files?: File[],
  ) => Promise<unknown>;
  comment?: Comment;
  isPending?: boolean;
  isReply?: boolean;
  isReplyInput?: boolean;
  objectModel?: "post" | "comment";
  replies?: Comment[];
  onReplyClick?: (commentId: number) => void;
  reactingComments?: Set<number>;
  isSending?: boolean;
  onCommentSent?: () => void;
  onCancelInput?: () => void;
  isSubmitting?: boolean;
  autoFocus?: boolean;
  onDraftRestorationSettled?: () => void;
  studyGroupId?: string | number | null;
  topicIds?: Array<string | number>;
  onDeleteComment?: (commentId: number) => Promise<void>;
  onEditComment?: (
    commentId: number,
    content: string,
    fileOps?: EditableFileOps,
  ) => Promise<void>;
  isCurrentUserAuthor?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  currentUserId?: number | string;
  deletingComments?: Set<number>;
  editingComments?: Set<number>;
  onSendMessage?: (userId: number) => void;
  isCommentActive?: boolean;
  isHighlighted?: boolean;
  isNew?: boolean;
  isMention?: boolean;
  disabled?: boolean;
}

const isUnavailableDraftTargetError = (error: unknown): boolean => {
  const status = getApiErrorStatus(error);
  return status === 403 || status === 404;
};

export const getReplyThreadState = (reply: Comment) => ({
  isNew: Boolean(
    "is_unread" in reply &&
    reply.is_unread &&
    !("is_unread_mention" in reply && reply.is_unread_mention),
  ),
  isMention: Boolean("is_unread_mention" in reply && reply.is_unread_mention),
});

export const CommentCard: React.FC<CommentCardProps> = ({
  author,
  content,
  timestamp,
  avatarUrl,
  isInput = false,
  postId,
  draftPostId,
  replyParentCommentId,
  replyAnchorCommentId,
  commentId,
  currentReaction = null,
  onReaction,
  onAddComment,
  comment,
  isPending = false,
  isReply = false,
  isReplyInput = false,
  objectModel = "post",
  onReplyClick,
  replies,
  reactingComments = new Set<number>(),
  isSending = false,
  onCommentSent,
  onCancelInput,
  isSubmitting = false,
  autoFocus = true,
  onDraftRestorationSettled,
  studyGroupId,
  topicIds,
  onDeleteComment,
  onEditComment,
  isCurrentUserAuthor = false,
  canEdit,
  canDelete,
  currentUserId,
  deletingComments = new Set<number>(),
  editingComments = new Set<number>(),
  onSendMessage,
  isCommentActive = false,
  isHighlighted = false,
  isNew = false,
  isMention = false,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [draftTargetUnavailable, setDraftTargetUnavailable] = useState(false);
  // Extract properties from comment object if provided
  const displayAuthor =
    author ||
    comment?.user?.display_name ||
    comment?.createdBy?.display_name ||
    "";
  const displayContent = content || comment?.message || "";
  const originalContent =
    typeof comment?.message_original === "string" &&
    comment.message_original.trim().length > 0
      ? comment.message_original
      : null;
  const editContent = originalContent ?? displayContent;
  const translationToggle = useOriginalContentToggle([
    { value: displayContent, original: originalContent },
  ]);
  const { showOriginal, canToggleOriginal, toggleOriginal, getVisibleContent } =
    translationToggle;
  const visibleContent = getVisibleContent(displayContent, originalContent);
  const displayTimestamp =
    timestamp || comment?.created_at || comment?.createdAt || "";
  const formattedTimestamp = (() => {
    if (!displayTimestamp) return "";
    const { date, time } = convertTimezoneToLocal(displayTimestamp, "UTC");
    if (!date || !time) return displayTimestamp;
    return `${date} ${time}`;
  })();
  const displayAvatarUrl =
    avatarUrl ||
    comment?.user?.image_url ||
    comment?.createdBy?.image_url ||
    "";
  const displayUserId = comment?.user?.id || comment?.createdBy?.id || 0;
  const displayCommentId = commentId || comment?.id || 0;
  const displayReaction =
    currentReaction || comment?.reactions?.current_user_emotion || null;
  const commentReplies = replies ?? comment?.comments ?? [];
  const hasReplies = commentReplies.length > 0;
  const effectiveIsSending = getEffectiveIsSending(comment, isSending);
  const draftScope = (() => {
    const userId = Number(currentUserId);
    const numericStudyGroupId = Number(studyGroupId);
    const numericPostId = Number(draftPostId);
    const hasBaseScope =
      Number.isSafeInteger(userId) &&
      userId > 0 &&
      Number.isSafeInteger(numericStudyGroupId) &&
      numericStudyGroupId > 0 &&
      Number.isSafeInteger(numericPostId) &&
      numericPostId > 0;

    if (!hasBaseScope) return undefined;

    if (!isReplyInput) {
      return {
        kind: "comment",
        userId,
        studyGroupId: numericStudyGroupId,
        postId: numericPostId,
      } satisfies ComposerDraftScope;
    }

    const parentCommentId = Number(replyParentCommentId);
    const anchorCommentId = Number(replyAnchorCommentId);
    if (
      !Number.isSafeInteger(parentCommentId) ||
      parentCommentId <= 0 ||
      !Number.isSafeInteger(anchorCommentId) ||
      anchorCommentId <= 0
    ) {
      return undefined;
    }

    return {
      kind: "reply",
      userId,
      studyGroupId: numericStudyGroupId,
      postId: numericPostId,
      parentCommentId,
      anchorCommentId,
    } satisfies ComposerDraftScope;
  })();

  const isDeleting = deletingComments?.has(displayCommentId) || false;
  const isEditing = editingComments?.has(displayCommentId) || false;
  const isReacting = reactingComments?.has(displayCommentId) || false;

  const [_showReactions, setShowReactions] = useState(false);
  const [localReaction, setLocalReaction] = useState<ReactionType | null>(
    displayReaction,
  );
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const [isEditMode, setIsEditMode] = useState(false);
  const [editedMessage, setEditedMessage] = useState(editContent);
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const mentionNoticeResetKey = isEditMode
    ? `${displayCommentId}:${editContent}`
    : null;
  const hasTypedMentionInEdit = useMentionInputNotice(
    editedMessage,
    editContent,
    mentionNoticeResetKey,
  );

  // メンション機能用フック
  const {
    searchMentionAvailableUsers,
    data: mentionUsers,
    isLoading: isLoadingMentionUsers,
    mentionScopeKey,
    hasMore: hasMoreMentionUsers,
    isFetchingMore: isFetchingMoreMentionUsers,
    loadMoreError: mentionLoadMoreError,
    fetchNextMentionUsers,
  } = useStudyGroupMentionAvailableUsers(studyGroupId || null, {
    topicIds,
    limit: COMMENT_MENTION_PAGE_SIZE,
  });

  // Update localReaction when displayReaction changes from parent
  useEffect(() => {
    setLocalReaction(displayReaction);
  }, [displayReaction]);

  useEffect(() => {
    setEditedMessage(editContent);
  }, [editContent]);

  useEffect(() => {
    if (!isEditing) {
      setIsEditMode(false);
    }
  }, [isEditing]);

  const handleReactionSelect = async (reaction: ReactionType) => {
    if (!displayCommentId || !onReaction || disabled) {
      return;
    }

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setShowReactions(false);

    // Determine the new reaction state
    let newReactionState: ReactionType | null;

    if (localReaction === reaction) {
      // If clicking the same reaction, remove it
      newReactionState = null;
    } else {
      // Otherwise set the new reaction
      newReactionState = reaction;
    }

    // Update local state immediately for responsive UI
    setLocalReaction(newReactionState);

    // Call the parent handler with the new reaction state
    try {
      await onReaction(displayCommentId, newReactionState);
    } catch (error) {
      // Revert on error
      console.error("Reaction failed:", error);
      setLocalReaction(displayReaction);
    }
  };

  // BaseCommentInput用の送信ハンドラー
  const handleCommentSubmit = async (content: string, files?: File[]) => {
    if (!onAddComment) {
      console.error("Missing onAddComment handler");
      return;
    }

    if (!postId && !displayCommentId) {
      console.error("Missing postId or commentId for comment submission");
      return;
    }

    try {
      const targetId = postId || displayCommentId;
      if (!targetId) {
        console.error("No target ID available for comment submission");
        return;
      }

      const targetObjectModel = isReplyInput ? "comment" : objectModel;

      await onAddComment(content, targetId, targetObjectModel, files);

      if (onCommentSent) {
        onCommentSent();
      }
    } catch (error) {
      if (isUnavailableDraftTargetError(error)) {
        setDraftTargetUnavailable(true);
        if (draftScope) {
          void draftRepository.markTargetUnavailable(draftScope);
        }
      }
      console.error("Failed to add comment:", error);
      throw error;
    }
  };

  // Modified handleReplyButtonClick to only handle reply creation, not toggling visibility
  const handleReplyButtonClick = () => {
    if (onReplyClick && displayCommentId && !disabled) {
      onReplyClick(displayCommentId);
    }
  };

  const handleEditClick = (_commentId: number) => {
    setIsEditMode(true);
    setEditedMessage(editContent);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedMessage(editContent);
  };

  const handleSubmitEdit = async (
    nextMessage: string,
    fileOps: EditableFileOps,
  ) => {
    const trimmedMessage = nextMessage.trim();
    setEditedMessage(nextMessage);

    const hasFileChanges =
      fileOps.filesToRemove.length > 0 ||
      fileOps.filesToReplace.length > 0 ||
      fileOps.filesToAdd.length > 0;
    if (
      !onEditComment ||
      (!hasFileChanges && trimmedMessage === editContent.trim())
    ) {
      setIsEditMode(false);
      return;
    }

    if (trimmedMessage === "") {
      toast({ description: t("common.messageCannotBeEmpty") });
      return;
    }

    setIsEditMode(false);
    setIsSubmittingEdit(true);

    try {
      await onEditComment(displayCommentId, trimmedMessage, fileOps);
    } catch (error) {
      console.error("Failed to edit comment:", error);
      toast({ variant: "error", description: t("common.editError") });
      setEditedMessage(editContent);
      setIsEditMode(true);
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  const handleDeleteClick = async (commentId: number) => {
    if (!onDeleteComment) {
      console.error(" CommentCard: onDeleteComment handler is missing!");
      return;
    }

    try {
      await onDeleteComment(commentId);
    } catch (error) {
      console.error(" CommentCard: Failed to delete comment:", error);
    }
  };

  const renderInputCard = () => (
    <Card
      className={cn(
        "flex w-full flex-col rounded-none border-none bg-transparent shadow-none",
      )}
    >
      <CardContent className="p-3">
        <BaseCommentInput
          onSubmit={handleCommentSubmit}
          onCancel={onCancelInput}
          onMentionTrigger={searchMentionAvailableUsers}
          mentionScopeKey={mentionScopeKey}
          mentionUsers={mentionUsers}
          isLoadingMentionUsers={isLoadingMentionUsers}
          hasMoreMentionUsers={hasMoreMentionUsers}
          isFetchingMoreMentionUsers={isFetchingMoreMentionUsers}
          mentionLoadMoreError={mentionLoadMoreError}
          onLoadMoreMentionUsers={fetchNextMentionUsers}
          autoFocus={autoFocus}
          disabled={isSubmitting}
          draftScope={draftScope}
          draftTargetUnavailable={draftTargetUnavailable}
          onDraftRestorationSettled={onDraftRestorationSettled}
        />
      </CardContent>
    </Card>
  );

  if (isInput) {
    return renderInputCard();
  }

  // Apply different styles for replies
  const replyMarginClass = isReply ? "ml-8" : "";

  const isAnyActionLoading = getIsAnyActionLoading({
    isDeleting,
    isSubmittingEdit,
    isSending: effectiveIsSending,
  });

  return (
    <Card
      data-comment-id={displayCommentId}
      className={cn(
        "group/comment flex w-full min-w-0 flex-col overflow-visible rounded-2xl border-none shadow-none",
        isHighlighted ? "bg-amber-50" : "bg-transparent",
        isHighlighted &&
          "transition-colors duration-300 motion-reduce:transition-none",
        effectiveIsSending && "border-l-4 border-blue-500 bg-blue-50",
        isAnyActionLoading && "opacity-70",
        replyMarginClass,
      )}
    >
      <CardContent className="flex w-full min-w-0 flex-col gap-0 overflow-visible pb-2 pl-3 pt-3">
        <div className="flex w-full min-w-0 items-start gap-2">
          <UserAvatarWithPopup
            userId={displayUserId}
            userName={displayAuthor}
            avatarUrl={displayAvatarUrl}
            currentUserId={currentUserId}
            onSendMessage={onSendMessage}
            wrapperClassName="h-8 w-8 flex-shrink-0 cursor-pointer transition-opacity hover:opacity-80"
            avatarClassName="h-8 w-8 flex-shrink-0 rounded-full"
            avatarFallbackClassName="rounded-full text-sm"
          />
          <div className="flex w-full min-w-0 flex-1 flex-col overflow-visible">
            <div className="comment-header-offset relative flex min-w-0 items-center pr-9">
              <div className="flex min-w-0 items-center gap-2">
                <span className="truncate text-sm font-semibold text-variable-collection-black-gray">
                  {displayAuthor}
                </span>
                <span className="shrink-0 text-xs font-medium text-rw-dark-gray">
                  {formattedTimestamp}
                </span>
                <ActivityStatusBadge isNew={isNew} isMention={isMention} />
                {effectiveIsSending && <SendingBadge />}
                {canToggleOriginal && !isEditMode && (
                  <TranslationToggleButton
                    showOriginal={showOriginal}
                    onToggle={toggleOriginal}
                    className="shrink-0"
                  />
                )}
              </div>
              {!disabled && (
                <div className="absolute right-0 top-1/2 z-30 -translate-y-1/2">
                  <CommentHeaderActions
                    commentId={displayCommentId}
                    isCurrentUserAuthor={isCurrentUserAuthor}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    isAnyActionLoading={isAnyActionLoading}
                    isDeleting={isDeleting}
                    onEditClick={handleEditClick}
                    onDeleteClick={handleDeleteClick}
                  />
                </div>
              )}
            </div>
            {(() => {
              if (isEditMode) {
                return (
                  <div className="comment-body-offset">
                    <CommentEditForm
                      value={editedMessage}
                      isSubmitting={isSubmittingEdit}
                      onChange={setEditedMessage}
                      onSave={handleSubmitEdit}
                      onCancel={handleCancelEdit}
                      showMentionWarning={hasTypedMentionInEdit}
                      existingFiles={comment?.files}
                    />
                  </div>
                );
              }
              return (
                <div className="comment-body-offset">
                  <EventLinkPreviewProvider showOriginalContent={showOriginal}>
                    <ExpandableText content={visibleContent} />
                  </EventLinkPreviewProvider>

                  {/* Display file attachments if present */}
                  {comment?.files && comment.files.length > 0 && (
                    <div className="mt-2">
                      <MediaPreview files={comment.files} />
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        <InteractionFooter
          localReaction={localReaction}
          isReacting={isReacting}
          isPending={isPending}
          commentId={displayCommentId}
          onReactionSelect={handleReactionSelect}
          onReplyClick={handleReplyButtonClick}
          showReplyButton={true}
          isAnyActionLoading={isAnyActionLoading}
          reactionTotal={comment?.reactions?.total ?? 0}
          lastReactions={comment?.reactions?.last_reactions ?? []}
          reactionBreakdown={comment?.reactions?.by_emotion}
          reactionObjectModel={COMMENT_REACTION_OBJECT_MODEL}
          reactionObjectId={displayCommentId ?? undefined}
          isCommentActive={isCommentActive}
          disabled={disabled}
        />

        {/* Show replies if available */}
        {hasReplies && (
          <div className="mt-2 w-full max-w-full overflow-hidden rounded bg-transparent">
            {commentReplies.map((reply) => {
              const replyThreadState = getReplyThreadState(reply);
              const isReplyAuthor = (() => {
                if (!currentUserId) return false;
                const replyUserId = reply.user?.id || reply.createdBy?.id;
                const isAuthor = String(replyUserId) === String(currentUserId);
                return isAuthor;
              })();

              return (
                <ReplyCard
                  key={reply.id}
                  comment={reply}
                  onReaction={onReaction}
                  isReacting={reactingComments.has(reply.id)}
                  onReplyClick={onReplyClick}
                  isPending={reply.isPending}
                  isSending={reply.isSending}
                  onDeleteComment={onDeleteComment}
                  onEditComment={onEditComment}
                  isDeleting={deletingComments?.has(reply.id) || false}
                  isCurrentUserAuthor={isReplyAuthor}
                  canEdit={
                    (
                      reply as Comment & {
                        capabilities?: { can_edit: boolean };
                      }
                    ).capabilities?.can_edit
                  }
                  canDelete={
                    (
                      reply as Comment & {
                        capabilities?: { can_delete: boolean };
                      }
                    ).capabilities?.can_delete
                  }
                  currentUserId={currentUserId}
                  onSendMessage={onSendMessage}
                  disabled={disabled}
                  isNew={replyThreadState.isNew}
                  isMention={replyThreadState.isMention}
                />
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
